import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FactorEntry } from "../services/productFactorsService";
import {
  computeFactorPeriodReturn,
  getV3FilterWindowBounds,
  V3_FACTOR_SLOT_MS,
  V3_INVESTOR_TIME_FILTER_OPTIONS,
  type V3TimeFilter,
} from "../components/performanceChartV3Utils";
import { computeActualPeriodReturn } from "./useActualPerformance";
import {
  buildMergedChartData,
  getAvailableFilters,
  getSeriesAnchorMs,
  seriesHasFactorForPeriod,
  type SeriesInput,
  type StrategyComparisonChartDataResult,
} from "./useStrategyComparisonChartData";

export const ACTUAL_COMPARISON_ALL_KEY = "all";
export const ACTUAL_COMPARISON_STRATEGY_KEY = "strategy";
export const ACTUAL_COMPARISON_ACTUAL_KEY = "actual";

const STRATEGY_STROKE = "white";
const ACTUAL_STROKE = "#CC7A00";

const DEFAULT_CHART_HEIGHT = 560;
const DEFAULT_SELECTED_PERIOD: V3TimeFilter = "Since inception";

const TIME_FILTER_OPTIONS = V3_INVESTOR_TIME_FILTER_OPTIONS.filter(
  (option) => option.value !== "All"
);
const FILTER_VALUES = TIME_FILTER_OPTIONS.map(
  (option) => option.value
) as V3TimeFilter[];

export interface StrategyVsActualComparisonResult
  extends StrategyComparisonChartDataResult {
  strategyFactorsFromSharedStart: FactorEntry[];
  strategyTimeframePerformance: Record<string, number | null>;
  actualTimeframePerformance: Record<string, number | null>;
  actualSummaryFilters: V3TimeFilter[];
}

/**
 * Collapse an intraday factor series into one factor per UTC day (the product
 * of that day's factors), stamped at UTC midnight.
 *
 * Strategy factors arrive at 30-minute slots while actual performance is one
 * point per day. Overlaying them at sub-day buckets (1D/3D/1W/1M) makes the
 * strategy line look jagged next to the smooth daily actual line. Collapsing
 * both series to daily keeps the comparison apples-to-apples, and because every
 * point lands on UTC midnight, the two lines' bucket grids coincide on every
 * horizon so their vertices align exactly.
 *
 * The cumulative return is unchanged: the product of a day's factors is that
 * day's return, so the geometric total across days is identical.
 */
export function collapseToDaily(factors: FactorEntry[]): FactorEntry[] {
  if (factors.length === 0) return [];

  const factorByDay = new Map<string, number>();
  for (const factor of factors) {
    const parsedTime = new Date(factor.t).getTime();
    if (Number.isNaN(parsedTime)) continue;
    const value = typeof factor.f === "number" ? factor.f : Number(factor.f);
    if (!Number.isFinite(value)) continue;
    const day = new Date(parsedTime).toISOString().slice(0, 10);
    factorByDay.set(day, (factorByDay.get(day) ?? 1) * value);
  }

  return [...factorByDay.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([day, f]) => ({ t: `${day}T00:00:00.000Z`, f }));
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Bridge interior calendar gaps in an ascending UTC-daily factor series by
 * inserting a flat (factor 1.00, i.e. no-change) entry for every missing UTC day
 * between the first and last day present.
 *
 * The two series do not always share a calendar -- actual may lag, carry weekend
 * rows the strategy lacks, or skip a day -- and leaving those days out makes the
 * overlaid lines diverge or break. Filling missing days with 1.00 keeps both
 * lines contiguous on the same daily grid: a no-data day renders as flat rather
 * than a break or a sloped bridge. A factor of 1.00 is multiplicatively neutral,
 * so the cumulative return (and every metric derived from it) is unchanged.
 *
 * Only interior days are filled; the first and last present days are untouched,
 * so each line keeps its own free start and end.
 */
export function fillDailyGaps(daily: FactorEntry[]): FactorEntry[] {
  if (daily.length < 2) return daily;

  const sorted = [...daily].sort(
    (a, b) => new Date(a.t).getTime() - new Date(b.t).getTime()
  );

  const filled: FactorEntry[] = [sorted[0]];
  let prevMs = new Date(sorted[0].t).getTime();

  for (let i = 1; i < sorted.length; i++) {
    const currentMs = new Date(sorted[i].t).getTime();
    for (let ms = prevMs + DAY_MS; ms < currentMs; ms += DAY_MS) {
      filled.push({ t: new Date(ms).toISOString(), f: 1 });
    }
    filled.push(sorted[i]);
    prevMs = currentMs;
  }

  return filled;
}

export function useStrategyVsActualComparison({
  isActive,
  selectedKey,
  strategyFactors,
  actualFactors,
  strategyColor = STRATEGY_STROKE,
  actualColor = ACTUAL_STROKE,
}: {
  isActive: boolean;
  selectedKey: string;
  strategyFactors: FactorEntry[];
  actualFactors: FactorEntry[];
  strategyColor?: string;
  actualColor?: string;
}): StrategyVsActualComparisonResult {
  const [selectedPeriod, setSelectedPeriod] = useState<V3TimeFilter>(
    DEFAULT_SELECTED_PERIOD
  );
  const userSelectedPeriodRef = useRef(false);

  useEffect(() => {
    if (isActive) {
      userSelectedPeriodRef.current = false;
      setSelectedPeriod(DEFAULT_SELECTED_PERIOD);
    }
  }, [isActive]);

  // Actual performance is one point per day; collapse the 30-minute strategy
  // series (and, harmlessly, the already-daily actual series) to one factor per
  // UTC day so the two lines render at the same daily resolution and align.
  const dailyStrategyFactors = useMemo(
    () => collapseToDaily(strategyFactors),
    [strategyFactors]
  );
  const dailyActualFactors = useMemo(
    () => collapseToDaily(actualFactors),
    [actualFactors]
  );

  const commonStartMs = useMemo(() => {
    if (dailyStrategyFactors.length === 0 || dailyActualFactors.length === 0) {
      return null;
    }

    return Math.max(
      new Date(dailyStrategyFactors[0].t).getTime(),
      new Date(dailyActualFactors[0].t).getTime()
    );
  }, [dailyActualFactors, dailyStrategyFactors]);

  const sharedStartUtc = commonStartMs === null
    ? null
    : new Date(commonStartMs - DAY_MS).toISOString();

  const actualFactorsFromSharedStart = useMemo(
    () => commonStartMs === null
      ? dailyActualFactors
      : dailyActualFactors.filter(
          (factor) => new Date(factor.t).getTime() >= commonStartMs
        ),
    [commonStartMs, dailyActualFactors]
  );

  const strategyFactorsFromSharedStart = useMemo(
    () => {
      const sorted = [...strategyFactors].sort(
        (left, right) => new Date(left.t).getTime() - new Date(right.t).getTime()
      );
      return commonStartMs === null
        ? sorted
        : sorted.filter((factor) => new Date(factor.t).getTime() >= commonStartMs);
    },
    [commonStartMs, strategyFactors]
  );

  const strategyTimeframePerformance = useMemo(
    () => V3_INVESTOR_TIME_FILTER_OPTIONS.reduce<Record<string, number | null>>(
      (metrics, option) => {
        metrics[option.value] = computeFactorPeriodReturn(
          strategyFactorsFromSharedStart,
          option.value
        );
        return metrics;
      },
      {}
    ),
    [strategyFactorsFromSharedStart]
  );

  const actualTimeframePerformance = useMemo(
    () => V3_INVESTOR_TIME_FILTER_OPTIONS.reduce<Record<string, number | null>>(
      (metrics, option) => {
        metrics[option.value] = computeActualPeriodReturn(
          actualFactorsFromSharedStart,
          option.value
        );
        return metrics;
      },
      {}
    ),
    [actualFactorsFromSharedStart]
  );

  const actualSummaryFilters = useMemo<V3TimeFilter[]>(() => {
    if (actualFactorsFromSharedStart.length === 0) return [];

    const firstFactor = actualFactorsFromSharedStart[0];
    const lastFactor = actualFactorsFromSharedStart[actualFactorsFromSharedStart.length - 1];
    const anchor = new Date(lastFactor.t);
    const baselineMs = sharedStartUtc
      ? new Date(sharedStartUtc).getTime()
      : new Date(firstFactor.t).getTime() - V3_FACTOR_SLOT_MS;
    const todayAvailable = getAvailableFilters(
      [{
        id: ACTUAL_COMPARISON_ACTUAL_KEY,
        label: "Actual",
        color: actualColor,
        factors: actualFactorsFromSharedStart,
        startAtUtc: sharedStartUtc,
        todayWindow: "utc-date-inclusive",
      }],
      ["Today"]
    ).includes("Today");

    return V3_INVESTOR_TIME_FILTER_OPTIONS
      .map((option) => option.value)
      .filter((period) => {
        if (period === "All") return false;
        if (period === "Today") return todayAvailable;
        if (period === "Since inception") return true;

        const { start } = getV3FilterWindowBounds(period, anchor, null);
        return start !== null && baselineMs <= start.getTime();
      });
  }, [actualColor, actualFactorsFromSharedStart, sharedStartUtc]);

  const series = useMemo<SeriesInput[]>(() => {
    const output: SeriesInput[] = [];

    const includeStrategy =
      selectedKey === ACTUAL_COMPARISON_ALL_KEY ||
      selectedKey === ACTUAL_COMPARISON_STRATEGY_KEY;
    const includeActual =
      selectedKey === ACTUAL_COMPARISON_ALL_KEY ||
      selectedKey === ACTUAL_COMPARISON_ACTUAL_KEY;

    // Inner-join the two lines on their start whenever both source series exist,
    // regardless of which line is currently visible. Strategy, Actual, and All
    // therefore rebase to 0% at the same first shared UTC day (commonStart = max
    // of the two first days), and changing the selector only hides or shows a
    // line. Setting startAtUtc one DAY before commonStart aligns the baseline
    // with the daily bucket grid: each factor lands exactly on its own UTC
    // midnight, including the last one (so the rightmost segment is one day
    // wide like the rest -- a 30-min offset would clamp the last point to the
    // anchor and render the final segment as a near-vertical sliver against the
    // right Y axis). The engine's exclusive `<= baseline` window keeps the
    // common-start day's own return while clipping every factor before it (so an
    // actual series that predates the strategy has its lead-in dropped, and vice
    // versa). Ends stay free -- no trailing clip -- so each line runs to its own
    // last day and an honest "actual lags by N days" picture shows.
    //
    // Both series are gap-filled (fillDailyGaps) so a day one calendar has and
    // the other lacks bridges flat (factor 1.00) instead of breaking the line;
    // the overlay stays contiguous and the cumulative return is unchanged.
    if (includeStrategy && strategyFactorsFromSharedStart.length > 0) {
      output.push({
        id: ACTUAL_COMPARISON_STRATEGY_KEY,
        label: "Strategy",
        color: strategyColor,
        factors: selectedPeriod === "Today"
          ? strategyFactorsFromSharedStart
          : fillDailyGaps(dailyStrategyFactors),
        startAtUtc: sharedStartUtc,
      });
    }

    if (includeActual && dailyActualFactors.length > 0) {
      output.push({
        id: ACTUAL_COMPARISON_ACTUAL_KEY,
        label: "Actual",
        color: actualColor,
        factors: fillDailyGaps(dailyActualFactors),
        startAtUtc: sharedStartUtc,
        todayWindow: "utc-date-inclusive",
      });
    }

    return output;
  }, [
    actualColor,
    dailyActualFactors,
    dailyStrategyFactors,
    selectedKey,
    selectedPeriod,
    sharedStartUtc,
    strategyColor,
    strategyFactorsFromSharedStart,
  ]);

  const availableFilters = useMemo(
    () => getAvailableFilters(series, FILTER_VALUES),
    [series]
  );

  useEffect(() => {
    if (
      !userSelectedPeriodRef.current &&
      selectedPeriod !== DEFAULT_SELECTED_PERIOD &&
      availableFilters.includes(DEFAULT_SELECTED_PERIOD)
    ) {
      setSelectedPeriod(DEFAULT_SELECTED_PERIOD);
      return;
    }

    if (!availableFilters.includes(selectedPeriod)) {
      setSelectedPeriod(
        availableFilters.includes(DEFAULT_SELECTED_PERIOD)
          ? DEFAULT_SELECTED_PERIOD
          : "Since inception"
      );
    }
  }, [availableFilters, selectedPeriod]);

  const handleSelectPeriod = useCallback((value: V3TimeFilter) => {
    userSelectedPeriodRef.current = true;
    setSelectedPeriod(value);
  }, []);

  // Only render a line for the selected period if that series has a real factor
  // inside the period window. Without this, a trailing window (e.g. "1 day")
  // where one series lags past the window start would still emit that series'
  // synthetic baseline 0% point -- a false "0%" line that contradicts the
  // metric table (which shows "-" for an empty window). The anchor is taken over
  // the full series set so it matches buildMergedChartData's internal anchor;
  // the series carrying the latest factor always qualifies, so the anchor never
  // shifts when a lagging series is dropped.
  const seriesForPeriod = useMemo(() => {
    const anchorMs = getSeriesAnchorMs(series);
    if (anchorMs === null) return series;
    return series.filter((item) =>
      seriesHasFactorForPeriod(item, selectedPeriod, anchorMs)
    );
  }, [series, selectedPeriod]);

  const chartData = useMemo(
    () => buildMergedChartData(seriesForPeriod, selectedPeriod),
    [selectedPeriod, seriesForPeriod]
  );

  const lines = useMemo(
    () =>
      seriesForPeriod.map((item) => ({
        dataKey: item.id,
        stroke: item.color,
        strokeWidth: item.id === ACTUAL_COMPARISON_STRATEGY_KEY ? 2.5 : 1.8,
        label: item.label,
        showDots: false,
      })),
    [seriesForPeriod]
  );

  return {
    loading: false,
    error: null,
    chartData,
    lines,
    chartHeight: DEFAULT_CHART_HEIGHT,
    timeFilterOptions: TIME_FILTER_OPTIONS,
    availableFilters,
    selectedPeriod,
    handleSelectPeriod,
    strategyFactorsFromSharedStart,
    strategyTimeframePerformance,
    actualTimeframePerformance,
    actualSummaryFilters,
  };
}
